import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Truck,
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Phone,
  MapPin
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import AdminLayout from '../../components/admin/AdminLayout';
import { toast } from '../../hooks/use-toast';

const statusColors = {
  pending: 'bg-yellow-900 text-yellow-300',
  approved: 'bg-green-900 text-green-300',
  rejected: 'bg-red-900 text-red-300',
};

const statusLabels = {
  pending: 'Pendiente',
  approved: 'Aprobado',
  rejected: 'Rechazado',
};

const vehicleLabels = {
  motorcycle: '🏍️ Moto',
  bicycle: '🚲 Bicicleta',
  car: '🚗 Auto',
};

const AdminDeliveryDrivers = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(null);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const limit = 20;

  const loadDrivers = useCallback(async () => {
    try {
      setLoading(true);
      const [driversData, countData] = await Promise.all([
        adminAPI.getDeliveryDrivers({ skip: page * limit, limit, verification_status: statusFilter }),
        adminAPI.getDeliveryDriversCount({ verification_status: statusFilter })
      ]);
      setDrivers(driversData);
      setTotalCount(countData.count);
    } catch (err) {
      console.error('Error loading drivers:', err);
      toast({ title: 'Error', description: 'Error al cargar repartidores', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    loadDrivers();
  }, [loadDrivers]);

  const handleVerify = async (driverId, action) => {
    if (action === 'reject' && !rejectionReason.trim()) {
      toast({ title: 'Error', description: 'Debes proporcionar una razón de rechazo', variant: 'destructive' });
      return;
    }

    setActionLoading(true);
    try {
      await adminAPI.verifyDeliveryDriver(driverId, action, rejectionReason);
      toast({
        title: action === 'approve' ? '¡Repartidor aprobado!' : 'Repartidor rechazado',
        description: action === 'approve' 
          ? 'El repartidor ya puede recibir pedidos' 
          : 'Se ha notificado al repartidor'
      });
      setSelectedDriver(null);
      setRejectionReason('');
      loadDrivers();
    } catch (err) {
      toast({ title: 'Error', description: 'No se pudo procesar la solicitud', variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };

  const totalPages = Math.ceil(totalCount / limit);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Repartidores</h1>
            <p className="text-gray-400 mt-1">{totalCount} repartidores registrados</p>
          </div>
          <Button onClick={loadDrivers} variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>

        {/* Filters */}
        <Card className="bg-gray-800 border-gray-700 p-4">
          <div className="flex flex-wrap gap-2">
            <Button
              variant={statusFilter === null ? 'default' : 'outline'}
              onClick={() => { setStatusFilter(null); setPage(0); }}
              className={statusFilter === null ? 'bg-orange-500 hover:bg-orange-600' : 'border-gray-600 text-gray-300'}
              size="sm"
            >
              Todos
            </Button>
            {Object.entries(statusLabels).map(([key, label]) => (
              <Button
                key={key}
                variant={statusFilter === key ? 'default' : 'outline'}
                onClick={() => { setStatusFilter(key); setPage(0); }}
                className={statusFilter === key ? 'bg-orange-500 hover:bg-orange-600' : 'border-gray-600 text-gray-300'}
                size="sm"
              >
                {label}
              </Button>
            ))}
          </div>
        </Card>

        {/* Drivers table */}
        <Card className="bg-gray-800 border-gray-700 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-8 h-8 text-orange-500 animate-spin" />
            </div>
          ) : drivers.length === 0 ? (
            <div className="text-center py-12">
              <Truck className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No se encontraron repartidores</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Repartidor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Vehículo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Teléfono</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Estado</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Entregas</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {drivers.map((driver) => {
                    const profile = driver.delivery_profile || {};
                    return (
                      <tr key={driver._id} className="hover:bg-gray-700/50">
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <img
                              src={driver.avatar}
                              alt={driver.name}
                              className="w-10 h-10 rounded-full bg-gray-700"
                            />
                            <div>
                              <p className="text-white font-medium">{driver.name}</p>
                              <p className="text-gray-400 text-sm">{driver.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-white">
                          {vehicleLabels[profile.vehicle_type] || profile.vehicle_type}
                          {profile.vehicle_plate && (
                            <span className="text-gray-400 text-sm ml-2">({profile.vehicle_plate})</span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-gray-300">
                          {profile.phone_number || '-'}
                        </td>
                        <td className="px-4 py-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[profile.verification_status] || statusColors.pending}`}>
                            {statusLabels[profile.verification_status] || 'Pendiente'}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-white">
                          {profile.total_deliveries || 0}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-2">
                            {profile.verification_status === 'pending' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleVerify(driver._id, 'approve')}
                                  className="text-green-400 hover:text-green-300 hover:bg-green-900/30"
                                  title="Aprobar"
                                >
                                  <CheckCircle className="w-5 h-5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setSelectedDriver(driver)}
                                  className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                                  title="Rechazar"
                                >
                                  <XCircle className="w-5 h-5" />
                                </Button>
                              </>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedDriver(driver)}
                              className="text-gray-400 hover:text-white"
                              title="Ver detalles"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
              <p className="text-sm text-gray-400">
                Mostrando {page * limit + 1} - {Math.min((page + 1) * limit, totalCount)} de {totalCount}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="border-gray-600 text-gray-300"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= totalPages - 1}
                  className="border-gray-600 text-gray-300"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Driver detail / Rejection modal */}
        {selectedDriver && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="bg-gray-800 border-gray-700 w-full max-w-lg max-h-[80vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-white">
                    Detalles del Repartidor
                  </h2>
                  <Button
                    variant="ghost"
                    onClick={() => { setSelectedDriver(null); setRejectionReason(''); }}
                    className="text-gray-400 hover:text-white"
                  >
                    ✕
                  </Button>
                </div>

                <div className="space-y-4">
                  {/* Driver info */}
                  <div className="flex items-center gap-4">
                    <img
                      src={selectedDriver.avatar}
                      alt={selectedDriver.name}
                      className="w-16 h-16 rounded-full bg-gray-700"
                    />
                    <div>
                      <h3 className="text-lg font-semibold text-white">{selectedDriver.name}</h3>
                      <p className="text-gray-400">{selectedDriver.email}</p>
                    </div>
                  </div>

                  {/* Profile details */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-gray-400 text-sm">Vehículo</p>
                      <p className="text-white">{vehicleLabels[selectedDriver.delivery_profile?.vehicle_type]}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Placa</p>
                      <p className="text-white">{selectedDriver.delivery_profile?.vehicle_plate || '-'}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Teléfono</p>
                      <p className="text-white">{selectedDriver.delivery_profile?.phone_number}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Licencia</p>
                      <p className="text-white">{selectedDriver.delivery_profile?.license_number || '-'}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Estado</p>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[selectedDriver.delivery_profile?.verification_status]}`}>
                        {statusLabels[selectedDriver.delivery_profile?.verification_status]}
                      </span>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Entregas</p>
                      <p className="text-white">{selectedDriver.delivery_profile?.total_deliveries || 0}</p>
                    </div>
                  </div>

                  {/* Actions for pending drivers */}
                  {selectedDriver.delivery_profile?.verification_status === 'pending' && (
                    <div className="border-t border-gray-700 pt-4 space-y-4">
                      <h4 className="text-white font-medium">Acciones de verificación</h4>
                      
                      <div className="space-y-2">
                        <label className="text-gray-400 text-sm">Razón de rechazo (opcional)</label>
                        <Textarea
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.target.value)}
                          placeholder="Ej: Documentación incompleta, licencia vencida..."
                          className="bg-gray-700 border-gray-600 text-white"
                          rows={3}
                        />
                      </div>

                      <div className="flex gap-3">
                        <Button
                          onClick={() => handleVerify(selectedDriver._id, 'approve')}
                          disabled={actionLoading}
                          className="flex-1 bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Aprobar
                        </Button>
                        <Button
                          onClick={() => handleVerify(selectedDriver._id, 'reject')}
                          disabled={actionLoading || !rejectionReason.trim()}
                          variant="outline"
                          className="flex-1 border-red-600 text-red-400 hover:bg-red-900/30"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Rechazar
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDeliveryDrivers;
